/**
 * Losses Service
 *
 * API calls for Losses.
 * @see Sprint 13 - Pertes
 * @see US-13.6 - UI Page Pertes
 */

import { apiClient } from '@/api/client';
import type {
  LossListItem,
  LossDetail,
  CreateLossDto,
  QueryLossesDto,
  ApiSuccess,
  LossesListResponse,
  LossDetailResponse,
  CreateLossResponse,
  CancelLossResponse,
  LossKPIsResponse,
  CreateFinishedProductLossDto,
  FinishedProductLossResult,
  FinishedProductLossPreview,
} from '@bstock/shared';

// Re-export types for convenience
export type {
  LossListItem,
  LossDetail,
  CreateLossDto,
  QueryLossesDto,
  LossKPIsResponse,
  CreateFinishedProductLossDto,
  FinishedProductLossResult,
  FinishedProductLossPreview,
};

/**
 * Type pour les produits finis avec recette (autocomplete)
 */
export interface PosProductWithRecipeOption {
  id: string;
  name: string;
  posId: string;
  sku: string | null;
  hasRecipe: boolean;
  ingredientsCount: number;
}

export const lossesService = {
  /**
   * List losses with pagination and filters
   */
  async list(params: QueryLossesDto): Promise<LossesListResponse> {
    const response = await apiClient.get<ApiSuccess<LossesListResponse>>(
      '/losses',
      { params }
    );
    return response.data.data;
  },

  /**
   * Get losses KPIs
   */
  async getKPIs(): Promise<LossKPIsResponse> {
    const response = await apiClient.get<ApiSuccess<LossKPIsResponse>>(
      '/losses/kpis'
    );
    return response.data.data;
  },

  /**
   * Get single loss by ID
   */
  async getById(id: string): Promise<LossDetailResponse> {
    const response = await apiClient.get<ApiSuccess<LossDetailResponse>>(
      `/losses/${id}`
    );
    return response.data.data;
  },

  /**
   * Create a new loss
   */
  async create(data: CreateLossDto): Promise<CreateLossResponse> {
    const response = await apiClient.post<ApiSuccess<CreateLossResponse>>(
      '/losses',
      data
    );
    return response.data.data;
  },

  /**
   * Cancel a loss (creates reversal movement)
   */
  async cancel(id: string): Promise<CancelLossResponse> {
    const response = await apiClient.delete<ApiSuccess<CancelLossResponse>>(
      `/losses/${id}`
    );
    return response.data.data;
  },

  // ========================================
  // PERTES DE PRODUITS FINIS
  // ========================================

  /**
   * Search PosProducts with recipes for autocomplete
   */
  async searchPosProductsWithRecipe(
    search: string,
    limit: number = 10
  ): Promise<PosProductWithRecipeOption[]> {
    const response = await apiClient.get<ApiSuccess<PosProductWithRecipeOption[]>>(
      '/losses/finished-product/products',
      { params: { search, limit } }
    );
    return response.data.data;
  },

  /**
   * Preview finished product loss (before creation)
   */
  async previewFinishedProductLoss(
    posProductId: string,
    quantity: number
  ): Promise<FinishedProductLossPreview> {
    const response = await apiClient.post<ApiSuccess<FinishedProductLossPreview>>(
      '/losses/finished-product/preview',
      { posProductId, quantity }
    );
    return response.data.data;
  },

  /**
   * Create a finished product loss
   */
  async createFinishedProductLoss(
    data: CreateFinishedProductLossDto
  ): Promise<FinishedProductLossResult> {
    const response = await apiClient.post<ApiSuccess<FinishedProductLossResult>>(
      '/losses/finished-product',
      data
    );
    return response.data.data;
  },
};
